import { AssertEqualType } from '@lessonup/domain-utils';
import { z } from 'zod';
import { AnswerByCategoryPinSchema } from './answer-by-category-pin';
import { BaseTeacherPinSettingsSchema } from './base-teacher-pin';
import { DragAndDropPinSchema } from './drag-and-drop-pin';
import { FillInTheBlanksPinSchema, FillInTheBlanksTeacherPinSettingsSchema } from './fill-in-the-blanks-pin';
import { OpenQuestionPinSchema, OpenQuestionTeacherPinSettingsSchema } from './open-question-pin';
import { PinType } from './pin';
import { PollPinSchema } from './poll-pin';
import { QuizPinSchema, QuizTeacherPinSettingsSchema } from './quiz-pin';
import { SlidePinSchema, SlideTeacherPinSettingsSchema } from './slide-pin';
import {
  SortingQuestionPinAnswerSchema,
  SortingQuestionPinSchema,
  SortingQuestionPinSettingsSchema,
} from './sorting-question-pin';
import { UnsupportedPinSchema } from './unsupported-pin';
import { WordCloudPinSchema } from './word-cloud-pin';

export const QuizTeacherPinSchema = QuizPinSchema.extend(
  z.object({
    teacherSettings: QuizTeacherPinSettingsSchema,
  }).shape
);
export type QuizTeacherPin = z.infer<typeof QuizTeacherPinSchema>;

export const SlideTeacherPinSchema = SlidePinSchema.extend(
  z.object({
    teacherSettings: SlideTeacherPinSettingsSchema,
  }).shape
);
export type SlideTeacherPin = z.infer<typeof SlideTeacherPinSchema>;

// TODO: Add specific teacherSettings for each placeholder
export const OpenQuestionTeacherPinSchema = OpenQuestionPinSchema.extend(
  z.object({
    teacherSettings: OpenQuestionTeacherPinSettingsSchema,
  }).shape
);
export type OpenQuestionTeacherPin = z.infer<typeof OpenQuestionTeacherPinSchema>;

export const WordCloudTeacherPinSchema = WordCloudPinSchema.extend(
  z.object({
    teacherSettings: BaseTeacherPinSettingsSchema.extend(z.object({}).shape),
  }).shape
);
export type WordCloudTeacherPin = z.infer<typeof WordCloudTeacherPinSchema>;

export const AnswerByCategoryTeacherPinSchema = AnswerByCategoryPinSchema.extend(
  z.object({
    teacherSettings: BaseTeacherPinSettingsSchema.extend(z.object({}).shape),
  }).shape
);
export type AnswerByCategoryTeacherPin = z.infer<typeof AnswerByCategoryTeacherPinSchema>;

export const PollTeacherPinSchema = PollPinSchema.extend(
  z.object({
    teacherSettings: BaseTeacherPinSettingsSchema.extend(z.object({}).shape),
  }).shape
);
export type PollTeacherPin = z.infer<typeof PollTeacherPinSchema>;

export const FillInTheBlanksTeacherPinSchema = FillInTheBlanksPinSchema.extend(
  z.object({
    teacherSettings: FillInTheBlanksTeacherPinSettingsSchema,
  }).shape
);

export type FillInTheBlanksTeacherPin = z.infer<typeof FillInTheBlanksTeacherPinSchema>;

export const SortingQuestionTeacherPinSchema = SortingQuestionPinSchema.extend(
  z.object({
    teacherSettings: BaseTeacherPinSettingsSchema,
    settings: SortingQuestionPinSettingsSchema.extend(
      z.object({
        answers: z.record(
          z.string(),
          SortingQuestionPinAnswerSchema.extend(
            z.object({
              correctOrder: z.string(), // Fractional index
            }).shape
          )
        ),
      }).shape
    ),
  }).shape
);
export type SortingQuestionTeacherPin = z.infer<typeof SortingQuestionTeacherPinSchema>;

export const UnsupportedTeacherPinSchema = UnsupportedPinSchema.extend(
  z.object({
    teacherSettings: BaseTeacherPinSettingsSchema.extend(z.object({}).shape),
  }).shape
);

export const DragAndDropTeacherPinSchema = DragAndDropPinSchema.extend(
  z.object({
    teacherSettings: BaseTeacherPinSettingsSchema.extend(
      z.object({
        correctAnswers: z.record(z.string(), z.array(z.string())),
      }).shape
    ),
  }).shape
);
export type DragAndDropTeacherPin = z.infer<typeof DragAndDropTeacherPinSchema>;

export type UnsupportedTeacherPin = z.infer<typeof UnsupportedTeacherPinSchema>;

export const TeacherPinSchema = z.discriminatedUnion('type', [
  QuizTeacherPinSchema,
  SlideTeacherPinSchema,
  OpenQuestionTeacherPinSchema,
  WordCloudTeacherPinSchema,
  AnswerByCategoryTeacherPinSchema,
  PollTeacherPinSchema,
  FillInTheBlanksTeacherPinSchema,
  SortingQuestionTeacherPinSchema,
  DragAndDropTeacherPinSchema,
  UnsupportedTeacherPinSchema,
]);

export type TeacherPin = z.infer<typeof TeacherPinSchema>;

// If this gives you an error, update the TeacherPinSchema union
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ensureTypesAreInSync: AssertEqualType<PinType, TeacherPin['type']> = true;
